import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useApolloClient } from '@apollo/client';
import { EventName, useEventsContext } from 'context/events/EventsContext';
import { ScriptStudioSearchClearLocation } from 'services/eventTrackingServiceTypes';
import useSectionSearch from '../Sections/hooks/useSectionSearch';
import useSectionsSearchDocument, {
  readCachedSections,
  useLiveSectionsSearchDocument,
} from '../hooks/useSectionsSearchDocument';
import useSearchStaleness from '../hooks/useSearchStaleness';

interface ProjectSearchContextProps {
  /** the debounced query the section list is filtered by */
  query: string;
  setQuery: (value: string) => void;
  /** ids of sections matching `query`, or `null` when no search is active */
  matchedSectionIds: Set<string> | null;
  /**
   * total number of individual matches within the matched sections, or `null`
   * when no search is active or the query is too short to highlight
   */
  matchCount: number | null;
  /** rebuilds the search document - called on search bar focus */
  refreshSearchDocument: () => void;
  /** tracks the user opening the search bar - called on search bar focus */
  trackSearchFocused: (hadSearch: boolean) => void;
  clearSearch: (
    location: ScriptStudioSearchClearLocation,
    hadSearch: boolean
  ) => void;
  /**
   * total number of individual matches across the project as of right now.
   */
  liveMatchCount: number | undefined;
  /** whether the visible results no longer reflect the project */
  isStale: boolean;
  /** re-runs the search so the visible results match `liveMatchCount` */
  refreshResults: () => void;
}

const ProjectSearchContext = createContext<ProjectSearchContextProps | null>(
  null
);

/**
 * @description owns the active project section search so that the search bar (in
 * the project menu bar), the section list (in the studio body) and everything
 * that highlights matches can share it despite being rendered in sibling
 * subtrees. Owns both the input text and the debounced query so clearing the
 * search from either place stays consistent.
 */
export function ProjectSearchProvider({
  projectId,
  children,
}: {
  projectId: string;
  children: React.ReactNode;
}) {
  const [query, setQuery] = useState('');
  const { sectionsSearchDocument, refreshSectionsSearchDocument } =
    useSectionsSearchDocument(projectId);
  const searchResult = useSectionSearch(sectionsSearchDocument, query);
  const matchedSectionIds = searchResult?.matchedSectionIds ?? null;
  const matchCount = searchResult?.matchCount ?? null;
  const [eventTracking] = useEventsContext();
  const apolloClient = useApolloClient();
  const liveSectionsSearchDocument = useLiveSectionsSearchDocument(
    projectId,
    matchedSectionIds !== null
  );
  const { liveMatchCount, isStale } = useSearchStaleness({
    liveDocument: liveSectionsSearchDocument,
    matchedSectionIds,
    matchCount,
    query,
  });

  const trackSearchFocused = useCallback(
    (hadSearch: boolean) => {
      // refocusing a bar that still holds a query is the same search session, so
      // only an empty bar counts as the user starting a search
      if (hadSearch) return;
      eventTracking.track({
        type: EventName.ScriptStudioSearchFocused,
        data: {
          projectId,
          sectionCount: readCachedSections(apolloClient, projectId).length,
        },
      });
    },
    [eventTracking, apolloClient, projectId]
  );

  const clearSearch = useCallback(
    (location: ScriptStudioSearchClearLocation, hadSearch: boolean) => {
      // the clear button is visible while the bar is focused, so it can be
      // clicked with nothing typed - that is not a clear worth reporting
      setQuery('');
      if (!hadSearch) return;
      eventTracking.track({
        type: EventName.ScriptStudioSearchCleared,
        data: { projectId, location },
      });
    },
    [eventTracking, projectId]
  );

  /**
   * reports every time a query is run against the sections
   */
  const trimmedQuery = query.trim();
  useEffect(() => {
    if (!trimmedQuery) return;
    eventTracking.track({
      type: EventName.ScriptStudioSearchPerformed,
      data: {
        projectId,
        resultCount: matchedSectionIds?.size ?? 0,
        queryLength: trimmedQuery.length,
      },
    });
  }, [trimmedQuery, matchedSectionIds, eventTracking, projectId]);

  const value = useMemo(
    () => ({
      query,
      setQuery,
      matchedSectionIds,
      matchCount,
      refreshSearchDocument: refreshSectionsSearchDocument,
      trackSearchFocused,
      clearSearch,
      liveMatchCount,
      isStale,
      refreshResults: refreshSectionsSearchDocument,
    }),
    [
      query,
      matchedSectionIds,
      matchCount,
      refreshSectionsSearchDocument,
      trackSearchFocused,
      clearSearch,
      liveMatchCount,
      isStale,
    ]
  );

  return (
    <ProjectSearchContext.Provider value={value}>
      {children}
    </ProjectSearchContext.Provider>
  );
}

export function useProjectSearchContext() {
  const context = useContext(ProjectSearchContext);

  if (!context) {
    throw new Error(
      'useProjectSearchContext must be used within a ProjectSearchProvider'
    );
  }

  return context;
}

/**
 * @description whether a search is currently filtering the section list. Use
 * this to feed `searchActive` into the section abilities, which stay decoupled
 * from this context.
 */
export function useProjectSearchActive() {
  const { matchedSectionIds } = useProjectSearchContext();
  return matchedSectionIds !== null;
}

export default ProjectSearchContext;
